import { isBrowser } from "./utils";
const GLOBAL_CONTEXT_REGISTRY = "___CSG_GLOBAL_CONTEXT_REGISTRY";
/**
 * Stores data that can be accessed across MFEs.
 *
 * `GlobalContext` is not a single context object; rather, it stores a set
 * of contexts. These can be created with {@link registerGlobalContext} and
 * accessed with {@link useContext}.
 *
 * Care should be taken when deciding to store data in the global context.
 * Overuse of the global context could lead to bugs and/or overcomplicated
 * interdependencies.
 *
 * @api
 */
const GlobalContext = (function () {
    const instance = {};
    /**
     * @private
     * @returns {@link GlobalContext} singleton instance.
     */
    function getInstance() {
        if (isBrowser) {
            if (!window[GLOBAL_CONTEXT_REGISTRY]) {
                window[GLOBAL_CONTEXT_REGISTRY] = instance;
            }
            return window[GLOBAL_CONTEXT_REGISTRY];
        }
        return instance;
    }
    /**
     * Obtains a context object from the global store.
     *
     * @param contextId The name of the context to retrieve.
     * @param fallbackContext A factory function to create a default context if
     * no context exists under `contextId`. Contexts created this way are _not_
     * persisted or stored in `GlobalContext`.
     *
     * @returns The context object stored under `contextId`, or the object
     * returned by `fallbackContext` if `contextId` doesn't exist. If no context
     * exists under `contextId` and no fallback is provided, this returns
     * `undefined`.
     */
    function useContext(contextId, fallbackContext) {
        var _a;
        if (contextId) {
            return (_a = getInstance()[contextId]) !== null && _a !== void 0 ? _a : fallbackContext === null || fallbackContext === void 0 ? void 0 : fallbackContext();
        }
        else {
            return fallbackContext === null || fallbackContext === void 0 ? void 0 : fallbackContext();
        }
    }
    /**
     * Get a list of all context ids registered to the Global Context.
     *
     * @returns list of ids
     */
    function listContexts() {
        const _instance = getInstance();
        return Object.keys(_instance);
    }
    /**
     * Checks if a context exists under `contextId`.
     *
     * @param contextId The ID of the context
     * @returns `true` if there is an existing context stored under `contextId`, `false` otherwise.
     */
    function isExistingContextId(contextId) {
        const instance = getInstance();
        return contextId in instance;
    }
    /**
     * Creates a new context under `contextId`.
     *
     * @param contextId The identifier the context will be stored under
     * @param context The context to store
     *
     * @throws if a context already exists under `contextId`.
     */
    function registerGlobalContext(contextId, context) {
        if (isExistingContextId(contextId)) {
            throw new Error("Context already exists, we should not register the same context multiple time.");
        }
        const instance = getInstance();
        // console.log(`--- Globalcontext adding ${contextId} ----`);
        instance[contextId] = context;
    }
    /**
     * Removes a context from the global context store.
     *
     * If no context exists under this ID, this function is a no-op.
     *
     * @private
     *
     * @param contextId The identifier of the context to remove.
     *
     * @see {@link unregisterGlobalContext}
     */
    function deleteContext(contextId) {
        const instance = getInstance();
        if (contextId in instance) {
            // eslint-disable-next-line no-console
            console.log(`--- Globalcontext removing ${contextId} ----`);
            delete instance[contextId];
        }
    }
    /**
     * Removes one or more contexts from the global context store.
     *
     * @param contextId The identifier(s) to delete.
     */
    function unregisterGlobalContext(contextId) {
        if (Array.isArray(contextId)) {
            for (const id of contextId) {
                deleteContext(id);
            }
        }
        else {
            deleteContext(contextId);
        }
    }
    return {
        useContext,
        isExistingContextId,
        listContexts,
        registerGlobalContext,
        unregisterGlobalContext,
    };
})();
export default GlobalContext;
//# sourceMappingURL=index.js.map